// FleetMap: what an empty workspace shows (a new one, or a pane with none):
// the range's AI work as one board (FlowBoard), and under it, scrolled to,
// the cases that need a person (WelcomeTriage), then the
// work by every kind of thing it reached (FleetInventory), the pane's
// foot naming each part and lighting the one in view, and along the pane's
// top what the board is narrowed to (BoardScope), held there as the page
// scrolls. The board fills the pane's first screen and stretches with it:
// every column, card and strand is laid out for the room the pane has.
// Everything on the page reads what the board's search reaches; a card or a
// row picked narrows that search, and the dock lists what it reaches
// (ScopeList). The sidebar keeps a search of its own (lib/sidebarSearch.js).
import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FlowBoard, { STEPS, countsOf, useAgentHues } from "./FlowBoard.jsx";
import BoardScope, { SCOPE_BAR_H } from "./BoardScope.jsx";
import ScrollArea from "./ScrollArea.jsx";
import FleetInventory, { KINDS } from "./FleetInventory.jsx";
import ScopeList from "./ScopeList.jsx";
import WelcomeTriage, { TRIAGE_PART } from "./WelcomeTriage.jsx";
import WelcomeFrame from "./welcome/WelcomeFrame.jsx";
import { ViewSwitch } from "./welcome/parts.jsx";
import { WELCOME_VIEWS, keepWelcomeView, readWelcomeView } from "./welcome/views.js";
import { clausesOf, withoutClause } from "./SearchPalette.jsx";
import { useQuery } from "../lib/dataClient.js";
import { useRingCounts } from "../lib/ringCounts.js";
import { useSessionFacts } from "../lib/sessionFacts.js";
import { appendClause, holdsClause, setWorkspaceSearch, useReviewReach, useSearchScope, useWorkspacePicks, useWorkspaceSearch } from "../lib/workspaceSearch.js";
import { askDock, lendDock, takeDockBack, useDockBridge, useDockScope } from "../lib/dockBridge.js";
import { rangeBoundsMs } from "../lib/analyticsClient.js";
import { toApiRange } from "../lib/shell.js";
import { pipelineFilterText } from "../lib/queryPipeline.js";
import { usePaneFoot } from "../lib/paneFoot.js";

const MARGIN = 16;
// The bar saying what the board is narrowed to (BoardScope), held along the
// pane's top as the page scrolls, what scrolls going under it. What scrolls
// the page to a part stops it HEAD_GAP under the bar.
const HEAD_GAP = 10;
const HEAD = SCOPE_BAR_H + HEAD_GAP;
// The page scrolls as every mode's sidebar does, its bar laid over it; the
// parts strip as the tab strips do, sideways, its bar shown while it moves.
const PAGE_SCROLL = Object.freeze({ overflow: { x: "hidden" } });
const PARTS_SCROLL = Object.freeze({ overflow: { x: "scroll", y: "hidden" }, scrollbars: { autoHide: "scroll", autoHideDelay: 400 } });

// The board's middle column says who ran the work: the groups they are in,
// or the people, as this browser last chose (people at first).
const MIDDLE = ["group", "person"];
const MIDDLE_KEY = "origin-welcome-middle-v1";
const SWAPS = [null, MIDDLE.map((k) => STEPS[k]), null];
function readMiddle() {
  try {
    const saved = globalThis.localStorage?.getItem(MIDDLE_KEY);
    return MIDDLE.includes(saved) ? saved : "person";
  } catch {
    return "person";
  }
}

// The page's parts, a hairline cell each, as the pane's own foot cells are
// drawn, the part in view lit as the app's rail lights its mode (the pressed
// tint and a green bar, here along the cell's foot): in the pane's foot band
// where there is one, else in a band of the page's own. Too many for the
// strip's width, it follows the page: the part in view stays in sight, with
// room for a part either side. A click goes to a part.
const BAND_H = 32;
// The sections under the board, in the order the page goes down; the groups
// only for a tenant whose directory gives someone a department.
const KINDS_NO_GROUPS = KINDS.filter((k) => k.id !== "groups");
const SECTIONS = [TRIAGE_PART, ...KINDS];
const SECTIONS_NO_GROUPS = [TRIAGE_PART, ...KINDS_NO_GROUPS];
const partsOf = (sections) => [{ id: "overview", label: "Overview" }, ...sections];
// The directory changes slowly: whether it has groups is read again at most
// this often.
const DIRECTORY_STALE_MS = 10 * 60_000;
const PART_CELL = "flex shrink-0 items-center whitespace-nowrap border-l border-[color:var(--line-soft)] px-[10px] leading-[16px] transition-colors";
const PartsBar = memo(function PartsBar({ parts, active, onGo }) {
  const strip = useRef(null);
  const setStrip = useCallback((instance) => { strip.current = instance ? instance.elements().viewport : null; }, []);
  useEffect(() => {
    const view = strip.current;
    const cell = view?.querySelector(`[data-fleet-nav-part="${active}"]`);
    if (!view || !cell || view.scrollWidth <= view.clientWidth) return;
    const v = view.getBoundingClientRect();
    const c = cell.getBoundingClientRect();
    const x0 = c.left - v.left + view.scrollLeft;
    const room = Math.min(80, Math.max(0, (view.clientWidth - c.width) / 2));
    const left = x0 - room < view.scrollLeft ? x0 - room
      : x0 + c.width + room > view.scrollLeft + view.clientWidth ? x0 + c.width + room - view.clientWidth : null;
    if (left != null) view.scrollTo?.({ left: Math.max(0, left), behavior: "smooth" });
  }, [active]);
  return (
    <ScrollArea instanceRef={setStrip} options={PARTS_SCROLL} observeMutations={false} className="min-w-0 border-r border-[color:var(--line-soft)]" contentClassName="h-full">
      <nav className="flex h-full items-stretch text-[11px]" aria-label="Parts of the overview" data-fleet-nav="">
        {parts.map((part) => {
          const on = active === part.id;
          return (
            <button
              key={part.id}
              type="button"
              onClick={() => onGo(part.id)}
              aria-current={on ? "true" : undefined}
              className={`${PART_CELL} ${on ? "[background-image:linear-gradient(var(--green-soft),var(--green-soft))] text-[color:var(--ink)] shadow-[inset_0_-2px_0_var(--green)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
              data-fleet-nav-part={part.id}
            >
              {part.label}
            </button>
          );
        })}
      </nav>
    </ScrollArea>
  );
});

// The list's tab named for the search it lists: its clauses in the board's
// words.
const listLabel = (text) => {
  const words = clausesOf(text).pills.map((p) => `${p.negated ? "not " : ""}${p.value}`);
  return words.length ? words.join(" \u00b7 ") : "Every session";
};

// `inFront`: whether this pane is the one worked in, whose clicks the dock
// lists.
export default function FleetMap({ range = "1M", lost = false, onStartSearch, inFront = true }) {
  const scope = useSearchScope(range);
  const metrics = useQuery("getOverviewMetrics", { scopePath: scope.scopePath, range, query: scope.steer, byTrace: true }, { enabled: scope.ready });
  // How the first screen shows the work: the flow board, or another of the
  // views tried beside it, named under it.
  const [viewId, setViewId] = useState(readWelcomeView);
  // The rings are counted over all the search reaches; the flow board is
  // drawn from the newest of its sessions, one row each. Only the view shown
  // is read.
  const rings = viewId === "rings";
  const counted = useRingCounts(scope, range, { enabled: rings });
  const listed = useSessionFacts(scope, range, { enabled: !rings });
  const read = rings ? counted : listed;
  // What the board last read stays up, dimmed, while the next reading is
  // under way, so narrowing the search moves the board rather than blanking
  // it; before any reading there is the board's placeholder.
  // A view's own last reading only: the rings' counts are not the flow's rows.
  const last = useRef({ facts: null, view: null, metrics: null });
  if (read.status === "done") last.current = { ...last.current, facts: read, view: viewId };
  if (metrics.data) last.current.metrics = metrics;
  const lastRead = last.current.view === viewId ? last.current.facts : null;
  const facts = read.status === "loading" && lastRead ? { ...lastRead, stale: true } : read;
  const shownMetrics = metrics.data || !last.current.metrics ? metrics : { ...last.current.metrics, stale: true };
  // Drawn from the newest of what the search reaches, the board counts only
  // those: where review left every one, for the search box to count and the
  // review cards to say the whole of (a card of 2 drawn here, 7 in all, is
  // 7 drilled down to).
  const flagged = useReviewReach(range, scope.q, { enabled: !!facts.capped && scope.ready });
  const reached = useMemo(() => {
    if (!flagged) return null;
    const total = Number(metrics.data?.sessions);
    const some = flagged.met + flagged.not_verified + flagged.not_met + flagged.set_aside;
    return { ...flagged, none: Number.isFinite(total) ? Math.max(0, total - some) : null };
  }, [flagged, metrics.data]);
  // Groups only where the directory gives someone a department (until that is
  // read, none): elsewhere the board, the sections and the finder go by people.
  const directory = useQuery("getDirectoryGroups", {}, { staleMs: DIRECTORY_STALE_MS });
  const groups = !!directory.data?.any;
  const kinds = groups ? KINDS : KINDS_NO_GROUPS;
  const below = groups ? SECTIONS : SECTIONS_NO_GROUPS;
  const partList = useMemo(() => partsOf(below), [below]);
  // Which agents ran the work, the groups or the people who ran it, and where
  // review left it.
  const [middle, setMiddle] = useState(readMiddle);
  const shownMiddle = groups ? middle : "person";
  const steps = useMemo(() => [STEPS.agent, STEPS[shownMiddle], STEPS.review], [shownMiddle]);
  const onSwap = useCallback((_ci, def) => {
    const key = MIDDLE.find((k) => STEPS[k] === def);
    if (!key) return;
    setMiddle(key);
    try {
      globalThis.localStorage?.setItem(MIDDLE_KEY, key);
    } catch {
      // Not kept past this page: the choice still stands while it is open.
    }
  }, []);
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  const bounds = useMemo(() => {
    const { fromMs, toMs } = rangeBoundsMs(typeof range === "string" ? toApiRange(range) : range);
    return { from: fromMs, to: Math.min(toMs, Date.now()) };
  }, [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // What scrolls: the board's screen, then the sections; the overlay
  // scrollbars' viewport, marked for what looks for the page's scroll.
  const viewRef = useRef(null);
  const [scroller, setScroller] = useState(null);
  const setView = useCallback((instance) => {
    const el = instance ? instance.elements().viewport : null;
    el?.setAttribute?.("data-fleet-scroll", "");
    // What brings a part of the page into view stops it under the search.
    if (el?.style) el.style.scrollPaddingTop = `${HEAD}px`;
    viewRef.current = el;
    setScroller(el);
  }, []);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = scroller;
    if (!el) return undefined;
    const read = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scroller]);
  // What the board reaches, listed in the dock beside the chat (ScopeList):
  // a tab of its own for as long as this pane is in front, named for what
  // the board is narrowed to (every session, until a click narrows it), and
  // brought in front at each click. It has no close: there is always a
  // board to list.
  const dockScope = useDockScope();
  const dockOwner = useMemo(() => ({ board: "overview", scope: dockScope }), [dockScope]);
  const { slot: dockSlot } = useDockBridge();
  const [listAsk, setListAsk] = useState(0);
  const showList = useCallback(() => setListAsk((n) => n + 1), []);
  const [searchText] = useWorkspaceSearch();
  const listName = useMemo(() => listLabel(searchText), [searchText]);
  // Lent as what it lends changes, not each time it is drawn: drawn again
  // whenever the dock changes, two pages up at once would take the dock from
  // each other without end.
  useEffect(() => {
    if (!inFront) return;
    lendDock(dockOwner, { tabs: [{ id: "scope", label: listName }], active: "scope" });
  }, [inFront, dockOwner, listName]);
  useEffect(() => (inFront ? () => takeDockBack(dockOwner) : undefined), [inFront, dockOwner]);
  // Asked after it is lent, so the dock comes to a tab it holds.
  useEffect(() => {
    if (listAsk && inFront) askDock(dockOwner, "scope");
  }, [listAsk]); // eslint-disable-line react-hooks/exhaustive-deps
  // A card or a row picked narrows the search to it, and everything on the
  // page and in the sidebar's list follows; the one it is narrowed to
  // already, picked, widens it back out. The dock lists what it reaches.
  const onPick = useCallback((clause) => {
    setWorkspaceSearch((t) => (holdsClause(t, clause) ? withoutClause(t, clause) : appendClause(t, clause)), { now: true });
    showList();
  }, [showList]);
  // Where each section stands, for the bar at the foot, and the part being
  // read: the last whose top is at the top of the view, the last of all at
  // the end. A part gone to from the bar stays lit while the page moves to
  // it, though a short last part can only come part of the way up.
  const sections = useRef(new Map());
  const sectionRef = useCallback((id, el) => {
    if (el) sections.current.set(id, el);
    else sections.current.delete(id);
  }, []);
  const topOf = useCallback((id) => {
    const el = viewRef.current;
    const part = sections.current.get(id);
    return el && part ? part.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop : 0;
  }, []);
  const [active, setActive] = useState("overview");
  const held = useRef(null);
  // Where the page is moving to, gone to from the bar: a section read as it
  // comes near grows under the move, and a move aimed once stopped that much
  // short of the part. Aimed again as it goes, until it stops.
  const aim = useRef(null);
  const aimAt = useCallback((id) => (id === "overview" ? 0 : Math.max(0, topOf(id) - HEAD)), [topOf]);
  useEffect(() => {
    const el = scroller;
    if (!el) return undefined;
    let frame = 0;
    const spy = () => {
      frame = 0;
      if (held.current) return;
      const line = el.scrollTop + HEAD + 40;
      let at = "overview";
      for (const part of below) if (sections.current.has(part.id) && topOf(part.id) <= line) at = part.id;
      if (el.scrollTop > 0 && el.scrollTop + el.clientHeight >= el.scrollHeight - 2) at = below[below.length - 1].id;
      setActive(at);
    };
    const onScroll = () => {
      // The page moving to a part gone to: held until it stops a moment, and
      // aimed again where the part now stands.
      if (held.current) {
        clearTimeout(held.current);
        held.current = setTimeout(() => { held.current = null; aim.current = null; }, 200);
        const a = aim.current;
        const top = a ? aimAt(a.id) : null;
        if (a && Math.abs(top - a.top) > 1) {
          a.top = top;
          el.scrollTo?.({ top, behavior: "smooth" });
        }
      }
      if (!frame) frame = requestAnimationFrame(spy);
    };
    // The reader taking the page over lets the aim go.
    const letGo = () => { aim.current = null; };
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("wheel", letGo, { passive: true });
    el.addEventListener("touchstart", letGo, { passive: true });
    el.addEventListener("keydown", letGo);
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("wheel", letGo);
      el.removeEventListener("touchstart", letGo);
      el.removeEventListener("keydown", letGo);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [scroller, topOf, aimAt, below]);
  const goTo = useCallback((id) => {
    const top = aimAt(id);
    setActive(id);
    aim.current = { id, top };
    clearTimeout(held.current);
    held.current = setTimeout(() => { held.current = null; aim.current = null; }, 200);
    viewRef.current?.scrollTo?.({ top, behavior: "smooth" });
  }, [aimAt]);
  // A section's row drilled down to (or back out of) narrows the search, and
  // the page goes back up to the board, to see what it narrowed to.
  const onDrill = useCallback((clause) => {
    onPick(clause);
    viewRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [onPick]);
  // The finder hands over the search as it stands at the keypress (the
  // scope trails the typing), with the sessions picked into it; once started
  // from, the picks are let go.
  const [picks, setPicks] = useWorkspacePicks();
  // The opener as it stands, so the search box's start does not change (and
  // the box redraw) each time the pane does.
  const startRef = useRef(onStartSearch);
  startRef.current = onStartSearch;
  const onStart = useCallback((text) => {
    const q = pipelineFilterText(text ?? scope.q).trim();
    if (!q && !picks.length) return;
    startRef.current?.(q, picks);
    setPicks([]);
  }, [scope.q, picks, setPicks]);
  // The dock's list starts one as the board's search would, the same each
  // time the page is drawn, so the list is not drawn again for it.
  const startFromList = useCallback(() => onStart(), [onStart]);

  // The board takes the first screen; the sections start under it. The
  // parts go in the pane's foot, or a band of the page's own without one.
  const foot = usePaneFoot();
  // Laid out again for the pane's room at a lower priority than what moves
  // it: while a column's seam is dragged, the drag keeps the frames and the
  // board catches up as it can, rather than every frame waiting on the
  // board's whole layout.
  const room = useDeferredValue(size);
  const boardW = Math.max(480, room.w - 2 * MARGIN);
  const boardH = Math.max(560, room.h - 2 * MARGIN - SCOPE_BAR_H);
  const parts = <PartsBar parts={partList} active={active} onGo={goTo} />;
  // The agents' colours, the board's and the Agents section's alike.
  const hues = useAgentHues(facts);
  const onView = useCallback((id) => {
    setViewId(id);
    keepWelcomeView(id);
  }, []);
  const view = WELCOME_VIEWS.find((v) => v.id === viewId) || WELCOME_VIEWS[0];
  // The dock's list counts the sessions as the search box does: all the
  // search reaches, where the board is drawn from the newest of them.
  const listTotal = facts.stale || facts.status !== "done" ? null
    : facts.rings ? facts.rings.total : facts.capped ? countsOf(shownMetrics)?.sessions ?? null : facts.sessions.length;
  const views = useMemo(() => <ViewSwitch views={WELCOME_VIEWS} view={view.id} onView={onView} />, [view.id, onView]);
  return (
    <div className="flex h-full min-h-0 flex-col" data-workspace-overview="">
      <ScrollArea instanceRef={setView} options={PAGE_SCROLL} observeMutations={false} className="min-h-0 flex-1 bg-[color:var(--workspace-bg)]" contentClassName="isolate">
        {size.w ? (
          <>
            <div className="sticky top-0 z-[40] h-0" data-board-scope-dock="">
              <div className="absolute inset-x-0 top-0 border-b border-[color:var(--line-soft)] bg-[color:var(--workspace-bg)]" style={{ height: SCOPE_BAR_H, paddingLeft: MARGIN, paddingRight: MARGIN }} data-board-scope-bar="">
                <BoardScope height={SCOPE_BAR_H - 1} notice={lost ? "The workspace this pane showed is no longer kept here." : null} />
              </div>
            </div>
            <div className="relative" style={{ height: SCOPE_BAR_H + MARGIN + boardH + MARGIN }}>
              <div className="absolute" style={{ left: MARGIN, top: SCOPE_BAR_H + MARGIN }}>
                {view.Body ? (
                  <WelcomeFrame
                    view={view}
                    facts={facts}
                    width={boardW}
                    height={boardH}
                    range={range}
                    bounds={bounds}
                    onPick={onPick}
                    onPicked={showList}
                    hues={hues}
                    groups={groups}
                    onShowAll={goTo}
                    aside={views}
                  />
                ) : (
                  <FlowBoard
                    facts={facts}
                    steps={steps}
                    width={boardW}
                    height={boardH}
                    reached={reached}
                    onPick={onPick}
                    hues={hues}
                    groups={groups}
                    swaps={groups ? SWAPS : null}
                    onSwap={onSwap}
                    aside={views}
                  />
                )}
              </div>
            </div>
            <div className="grid gap-[40px]" style={{ padding: `${MARGIN}px ${MARGIN}px 48px` }}>
              {/* What is near the view is told by the page's own scroller,
                  waited for (undefined) until it is up. */}
              <WelcomeTriage scope={scope} range={range} root={scroller ?? undefined} hold={read.status === "loading"} sectionRef={sectionRef} />
              <FleetInventory kinds={kinds} scope={scope} width={boardW} root={scroller ?? undefined} hold={read.status === "loading"} onDrill={onDrill} sectionRef={sectionRef} bounds={bounds} hues={hues} range={range} />
            </div>
          </>
        ) : null}
      </ScrollArea>
      {inFront && dockSlot ? createPortal(<ScopeList range={range} q={scope.q} total={listTotal} onStart={scope.q ? startFromList : null} />, dockSlot) : null}
      {foot ? createPortal(parts, foot) : (
        <div className="flex shrink-0 justify-center border-t border-[color:var(--line-soft)] bg-[color:var(--bg)]" style={{ height: BAND_H }}>{parts}</div>
      )}
    </div>
  );
}
